"use client";

import React, { useMemo, memo } from 'react';
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "~/components/ui/carousel";
import GlowingButton from "~/components/reusables/glowing-button";
import { LinkTrackers } from "~/components/reusables/link-trackers";
import SmartImage from "~/components/reusables/SmartImage";

interface NormalizedResource {
  id: string | number;
  type: "Blog" | "Event" | "Video" | "Whitepaper" | "Others"; // Added Others
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  ctaText: string;
  ctaLink: string;
  target: "_self" | "_blank";
}

interface ResourcesSectionProps {
  data: {
    related_blogs?: any[];
    related_events?: any[];
    related_videos?: any[];
    related_Whitepapers?: any[];
    related_githubs?: any[]; // Added GitHub field
  };
}

const ResourceCard = memo(({ resource }: { resource: NormalizedResource }) => {
  return (
    <article className="flex flex-col rounded-2xl border border-[#B9B9B9]/30 p-6 sm:p-8 lg:p-10 bg-white h-full w-full transition-all duration-300 hover:shadow-xl hover:border-blue-400/30 group">
      <div className="flex-1 flex flex-col">
        
        <div className="relative w-full aspect-video overflow-hidden rounded-xl border border-black/5 shadow-inner bg-gray-50 mb-6">
          <SmartImage 
            src={resource.image || "/images/pages/about-us/placeholder.png"} 
            alt={resource.title} 
            fill
            className="object-cover transition-transform duration-700 group-hover:scale-105"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-base font-medium text-black uppercase tracking-wide">
            {resource.eyebrow}
          </span>
        </div>
        
        <h3 className="text-xl font-light text-black leading-tight mb-4 group-hover:text-blue-700 transition-colors">
          {resource.title}
        </h3>

        <div className="">
          <p className="text-sm md:text-base font-light leading-relaxed opacity-90 line-clamp-3">
            {resource.description}
          </p>
        </div>
        
        <div className="pt-6">
          <GlowingButton className="w-full sm:w-max">
            <LinkTrackers
              href={resource.ctaLink}
              target={resource.target}
              segmentMsg="Clicked the Resource"
              segmentOpt={{
                link_label: resource.ctaText,
                content_type: resource.type,
                location: "resources_section",
              }}
              className="w-full block text-center py-2.5 font-light text-base md:text-lg"
            >
              {resource.ctaText}
            </LinkTrackers>
          </GlowingButton>
        </div>
      </div>
    </article>
  );
});

ResourceCard.displayName = "ResourceCard";

export function ResourcesSection({ data }: ResourcesSectionProps) {
  const allResources = useMemo(() => {
    if (!data) return [];

    const blogs = (data.related_blogs || []).map((blog: any): NormalizedResource => ({
      id: blog.documentId || blog.id,
      type: "Blog",
      eyebrow: "BLOG",
      title: blog.title || "",
      description: blog.articleSummary || "Read the latest insights from our engineering teams.",
      image: blog.thumbnail?.url || "",
      ctaText: "Read the Blog",
      ctaLink: `https://outshift.cisco.com/blog/${blog.key}`,
      target: "_self"
    }));

    const events = (data.related_events || []).map((event: any): NormalizedResource => ({
      id: event.documentId || event.id,
      type: "Event",
      eyebrow: "WEBINAR",
      title: event.title || "",
      description: event.blurb?.[0]?.children?.[0]?.text || "Join us for this upcoming session.",
      image: event.picture?.url || "",
      ctaText: "View Event",
      ctaLink: `https://outshift.cisco.com/events/${event.documentId}`,
      target: "_self"
    }));

    const videos = (data.related_videos || []).map((video: any): NormalizedResource => ({
      id: video.documentId || video.id,
      type: "Video",
      eyebrow: "VIDEO",
      title: video.title || "",
      description: video.description || "",
      image: video.thumbnail?.url || "",
      ctaText: "Watch Video",
      ctaLink: video.videoLink || "#",
      target: "_blank"
    }));

    const whitepapers = (data.related_Whitepapers || []).map((wp: any): NormalizedResource => ({
      id: wp.documentId || wp.id,
      type: "Whitepaper",
      eyebrow: "WHITEPAPER",
      title: wp.title || "",
      description: wp.description || "",
      image: wp.thumbnail?.url || "",
      ctaText: wp.cta?.cta || "Download Whitepaper",
      ctaLink: wp.cta?.url?.trim() || "#",
      target: "_blank"
    }));

    const githubs = (data.related_githubs || []).map((github: any): NormalizedResource => {
      const ctaLink = github.url || "#";
      
      const isGithub = ctaLink.toLowerCase().includes("github");
      const dynamicEyebrow = isGithub ? "GITHUB" : "OTHERS";

      return {
        id: github.documentId || github.id,
        type: "Others",
        eyebrow: dynamicEyebrow,
        title: github.title || "",
        description: github.description || "",
        image: github.featureImage?.url || "",
        ctaText: github.CTALabel || "Visit GitHub",
        ctaLink: ctaLink,
        target: "_blank"
      };
    });

    return [...blogs, ...events, ...videos, ...whitepapers, ...githubs];
  }, [data]);

  if (allResources.length === 0) return null;

  return (
    <section 
      id="resources"
      className="container mx-auto z-10 py-12 md:py-16 bg-[#f8f9fa] text-black overflow-hidden"
      aria-labelledby="resources-title"
    >
      <div className="">
        <header className="mb-10 lg:mb-12">
          <h2 id="resources-title" className="text-3xl md:text-4xl lg:text-[42px] font-light tracking-tight">
            Resources
          </h2>
        </header>

        <Carousel
          opts={{ align: "start", loop: false, containScroll: "trimSnaps" }}
          className="w-full"
        >
          <CarouselContent className="flex flex-col md:flex-row -ml-0 md:-ml-6 lg:-ml-8 gap-8 md:gap-0 items-stretch">
            {allResources.map((resource) => (
              <CarouselItem 
                key={`${resource.type}-${resource.id}`} 
                className="w-full md:basis-1/2 lg:basis-1/3 pl-0 md:pl-6 lg:pl-8 flex"
              >
                <ResourceCard resource={resource} />
              </CarouselItem>
            ))}
          </CarouselContent>
          
          <div className="hidden md:flex items-center gap-4 mt-12">
            <CarouselPrevious className="static translate-y-0 translate-x-0 border-black/20 hover:bg-black hover:text-white transition-all h-12 w-12" />
            <CarouselNext className="static translate-y-0 translate-x-0 border-black/20 hover:bg-black hover:text-white transition-all h-12 w-12" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}